// src/pages/internal/clientesSwap/previewData.js
import {
	Grid,
	Box,
	Typography,
	Tooltip,
	useTheme,
	Divider,
	Chip,
	Paper,
	IconButton
} from "@mui/material";
import useStyles from "./styles";
import {
	EditNoteOutlined,
	InfoOutlined,
	InventoryOutlined,
	PeopleOutline,
	BusinessOutlined,
	BadgeOutlined,
	RouteOutlined,
	CableOutlined,
	LayersOutlined,
	SpeedOutlined,
	RouterOutlined,
	AccountTreeOutlined,
	LanOutlined,
	FormatListNumberedOutlined,
	LeakAddOutlined,
	CachedOutlined,
	ContactPhoneOutlined,
	OpenInNewOutlined
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import DatagridComponent from "../../../components/datagridComponent";
import ConfusedMale from "../../../components/artsPersonComponent/confusedMale";
import { maskCellPhone, maskCnpj } from "../../../utils/masks";

export default function PreviewData({ form, setSnackbar }) {
	const theme = useTheme();
	const styles = useStyles(theme);
	const navigate = useNavigate();

	// 1. Extrai o objeto completo da tag para podermos pegar tanto o nome quanto a cor
	const parceiroTag = form?.tags?.find(t => t.type === "parceiroSwap");
	const parceiroNome = parceiroTag?.tagName || "-";
	const parceiroColor = parceiroTag?.color;

	const cedenteTag = form?.tags?.find(t => t.type === "cedenteSwap");
	const cedenteNome = cedenteTag?.tagName || "-";
	const cedenteColor = cedenteTag?.color;

	// 2. Função auxiliar para converter "colors.green.light" no HEX real do seu tema
	const getThemeColor = (colorPath) => {
		if (!colorPath) return undefined;
		// Navega pelo objeto theme.palette usando a string recebida (ex: colors.pink.medium)
		return colorPath.split('.').reduce((acc, part) => acc && acc[part], theme.palette) || colorPath;
	};

	async function CopyTexto({ campo }) {
		if (!campo || campo === "-") return;

		try {
			await navigator.clipboard.writeText(campo);
		} catch (err) {
			const textArea = document.createElement("textarea");
			textArea.value = campo;
			document.body.appendChild(textArea);
			textArea.select();
			try {
				document.execCommand("copy");
			} catch (err) {
				console.error("Falha ao copiar texto: ", err);
			}
			document.body.removeChild(textArea);
		}
		setSnackbar({
			children: "Campo copiado!",
			severity: "info",
		});
	}

	// 3. Recebendo a nova prop chipColor
	const GridItem = ({ title, value, icon: Icon, isChip = false, chipColor, ...props }) => {
		// Resolve a cor. Se existir, pega do tema
		const bgColor = chipColor ? getThemeColor(chipColor) : undefined;

		return (
			<Grid item {...props}>
				<Box sx={styles.boxCampos}>
					{/* Renderiza o ícone dinamicamente */}
					{Icon && <Icon sx={styles.icons} />}
					<Typography sx={styles.typhographyTitle}>{title}</Typography>
					<Tooltip title="Clique para copiar">
						{/* Se for Chip e tiver valor válido, renderiza o Chip. Senão, renderiza o Typography normal */}
						{isChip && value && value !== "-" ? (
							<Chip
								onClick={() => CopyTexto({ campo: value })}
								sx={{
									...styles.typographyLabel,
									cursor: "pointer",
									...(bgColor && {
										backgroundColor: bgColor,
										color: "#fff", // Define branco para contrastar com cores de fundo
										"&:hover": {
											backgroundColor: bgColor,
											filter: "brightness(0.9)" // Pequeno feedback de hover
										}
									})
								}}
								label={value}
								size="small"
							/>
						) : (
							<Typography
								sx={styles.typographyLabel}
								onClick={() => CopyTexto({ campo: value || "-" })}
							>
								{value ?? "-"}
							</Typography>
						)}
					</Tooltip>
				</Box>
			</Grid>
		);
	};

	return (
		<>
			{form ? (
				<>
					<>
						<Grid item xs={12} sx={styles.boxContent}>
							<CachedOutlined color="primary" />
							<Typography variant="mySubtitle2">
								Relação de sessão
							</Typography>
						</Grid>

						<Grid container spacing={1} columns={12}>
							{/* Passamos o isChip={true} e a cor correspondente para Parceiro e Cedente */}
							<GridItem xs={12} sm={4} smx={4} md={4} title="Parceiro:" value={parceiroNome} icon={PeopleOutline} isChip={true} chipColor={parceiroColor} />
							<GridItem xs={12} sm={4} smx={4} md={4} title="Cedente:" value={cedenteNome} icon={BusinessOutlined} isChip={true} chipColor={cedenteColor} />
							<GridItem xs={12} sm={4} smx={4} md={4} title="Serviço ID:" value={form?.serviceId} icon={BadgeOutlined} />
						</Grid>

						{/* --- CONTATOS DE ESCALONAMENTO (Parceiro / Cedente) --- */}
						{[
							{ key: "parceiro", title: "Contatos de Escalonamento — Parceiro", contacts: parceiroTag?.escalationContacts || [] },
							{ key: "cedente", title: "Contatos de Escalonamento — Cedente", contacts: cedenteTag?.escalationContacts || [] },
						].filter(s => s.contacts.length > 0).map((section) => (
							<Box key={section.key}>
								<Divider sx={styles.divider} />
								<Box sx={styles.boxContent}>
									<ContactPhoneOutlined color="primary" />
									<Typography variant="mySubtitle2">{section.title}</Typography>
								</Box>
								<Paper sx={{ padding: 2, borderRadius: 4, backgroundColor: theme.palette.background.wallpaper }} variant="outlined">
									<Grid container spacing={1} columns={12}>
										{section.contacts.map((c, index) => {
											const minutes = Number(c?.escalationTime) || 0;
											const hh = String(Math.floor(minutes / 60)).padStart(2, "0");
											const mm = String(minutes % 60).padStart(2, "0");
											const cellFields = [
												{ title: "Nome", value: c?.name, icon: BadgeOutlined },
												{ title: "Cargo", value: c?.role, icon: PeopleOutline },
												{ title: "Telefone", value: c?.phone ? maskCellPhone(c.phone) : "-", icon: ContactPhoneOutlined },
												{ title: "Tempo", value: `${hh}:${mm}`, icon: EditNoteOutlined },
											];
											return (
												<Grid item xs={12} key={c?.id || index}>
													<Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: "16px", p: 1.5, mb: 1, backgroundColor: theme.palette.background.paper }}>
														<Grid container spacing={1} columns={12} alignItems="flex-start">
															{cellFields.map((f) => (
																<Grid item xs={12} sm={6} md={3} key={f.title} sx={{ minWidth: 0 }}>
																	<Box sx={{ ...styles.boxCampos, alignItems: "flex-start", flexWrap: "wrap", minWidth: 0 }}>
																		<f.icon sx={styles.icons} />
																		<Typography sx={styles.typhographyTitle}>{f.title}:</Typography>
																		<Tooltip title="Clique para copiar">
																			<Typography
																				onClick={() => CopyTexto({ campo: f.value || "-" })}
																				sx={{
																					...styles.typographyLabel,
																					maxWidth: "100%",
																					whiteSpace: "normal",
																					wordBreak: "break-word",
																					overflowWrap: "anywhere",
																				}}
																			>
																				{f.value ?? "-"}
																			</Typography>
																		</Tooltip>
																	</Box>
																</Grid>
															))}
														</Grid>
													</Box>
												</Grid>
											);
										})}
									</Grid>
								</Paper>
							</Box>
						))}

						<Divider sx={styles.divider} />

						{/* Coluna 2 */}
						<Box sx={styles.boxContent}>
							<InventoryOutlined color="primary" />
							<Typography variant="mySubtitle2">Tipos de Swap</Typography>
							<Tooltip title="Entregas lógicas: MPLS, L2, entre outras." arrow>
								<InfoOutlined
									sx={{
										color: theme.palette.tertiary?.light,
										fontSize: 18,
										cursor: "pointer",
									}}
								/>
							</Tooltip>
						</Box>

						<Grid container spacing={1}>
							<Grid item xs={12}>
								<Box sx={styles.boxCampos}>
									<Tooltip title="Clique para copiar">
										<Box sx={styles.boxChip}>
											{form?.options?.map(
												(serviceType, index) =>
													serviceType?.label && (
														<Chip
															onClick={() =>
																CopyTexto({ campo: serviceType?.label })
															}
															key={index}
															sx={{ ...styles.typographyLabel, cursor: 'pointer' }}
															label={serviceType?.label}
															size="small"
														/>
													),
											)}
										</Box>
									</Tooltip>
								</Box>
							</Grid>
						</Grid>

						<Divider sx={styles.divider} />

						<Grid container spacing={1} columns={12}>
							<GridItem xs={12} sm={4} smx={4} md={4} title="Distância (km):" value={form?.distancia} icon={RouteOutlined} />
							<GridItem xs={12} sm={4} smx={4} md={4} title="Quantidade de Fibras:" value={form?.quantidadeDeFibras} icon={CableOutlined} />
							<GridItem xs={12} sm={4} smx={4} md={4} title="Total de Fibras (km):" value={form?.totalDeFibras} icon={LayersOutlined} />
							<GridItem xs={12} sm={4} smx={4} md={4} title="Capacidade (Gbps):" value={form?.capacidade} icon={SpeedOutlined} />
							<GridItem xs={12} sm={4} smx={4} md={4} title="UR's:" value={form?.urs} icon={RouterOutlined} />
						</Grid>

						<Divider sx={styles.divider} />

						{!form.parentId && form.subclientes && form.subclientes.length > 0 && (
							<>
								<Box sx={styles.boxContent}>
									<AccountTreeOutlined color="primary" />
									<Typography variant="mySubtitle2">
										Subtrechos:
									</Typography>
								</Box>
								<Paper sx={{ padding: 2, borderRadius: 4, backgroundColor: theme.palette.background.wallpaper }} variant="outlined" >
									<Grid container spacing={1} columns={12}>
										{form.subclientes.map((sub, index) => (
											<Grid item xs={12} key={index}>
												<Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: "16px", p: 1.5, mb: 1, backgroundColor: theme.palette.background.paper }}>
													<Grid container spacing={1} columns={12}>
														<GridItem xs={12} sm={4} smx={4} md={4} title="Posição" value={sub?.posicao} icon={FormatListNumberedOutlined} />
														<GridItem xs={12} sm={4} smx={4} md={4} title="Subtrecho" value={sub?.nome} icon={LanOutlined} />
														<GridItem xs={12} sm={4} smx={4} md={4} title="CNL" value={sub?.cnl} icon={LeakAddOutlined} />

													</Grid>
												</Box>
											</Grid>
										))}
									</Grid>
								</Paper>
								<Divider sx={styles.divider} />
							</>
						)}

						{Array.isArray(form?.clientesAfetados) && form.clientesAfetados.length > 0 && (
							<>
								<Box sx={styles.boxContent}>
									<BusinessOutlined color="primary" />
									<Typography variant="mySubtitle2">
										Clientes Afetados:
									</Typography>
								</Box>
								<Paper
									variant="outlined"
									sx={{
										width: "100%",
										overflow: "hidden",
										backgroundColor: theme.palette.background.paper,
										borderRadius: "16px",
									}}
								>
									<DatagridComponent
										rows={form.clientesAfetados}
										columns={[
											{
												field: "clienteErpId",
												headerName: "Código",
												width: 110,
												renderCell: (params) => params.row?.clienteErpId ?? "-",
											},
											{
												field: "cliente",
												headerName: "Cliente",
												flex: 1,
												minWidth: 200,
												renderCell: (params) => params.row?.nome || "-",
											},
											{
												field: "cnpj",
												headerName: "CNPJ",
												width: 180,
												renderCell: (params) => params.row?.cnpj ? maskCnpj(params.row.cnpj) : "-",
											},
											{
												field: "acoes",
												headerName: "Perfil",
												width: 90,
												sortable: false,
												filterable: false,
												disableColumnMenu: true,
												align: "center",
												headerAlign: "center",
												renderCell: (params) => params.row?.id ? (
													<Tooltip title="Abrir perfil do cliente">
														<IconButton
															size="small"
																onClick={(e) => { e.stopPropagation(); navigate(`/interno/home/perfil/servicos/${params.row.id}`); }}
														>
															<OpenInNewOutlined fontSize="small" />
														</IconButton>
													</Tooltip>
												) : null,
											},
										]}
										isPagination={form.clientesAfetados.length > 8}
										sxDatagrid={{ border: 0 }}
									/>
								</Paper>
								<Divider sx={styles.divider} />
							</>
						)}

						<Box sx={styles.boxContent}>
							<EditNoteOutlined color="primary" />
							<Typography variant="mySubtitle2">
								Observações Finais :
							</Typography>
						</Box>

						<Grid container spacing={1} columns={12}>
							<Grid item xs={12}>
								<Box sx={styles.boxCampos}>
									<Tooltip title="Clique para copiar">
										<Typography
											sx={styles.observationsTypography}
											onClick={() =>
												CopyTexto({ campo: form?.observacao || "-" })
											}
										>
											{form?.observacao || "-"}
										</Typography>
									</Tooltip>
								</Box>
							</Grid>
						</Grid>
					</>
				</>
			) : (
				<ConfusedMale
					mainFill={theme.palette.secondary.dark}
					primaryCircleFill={theme.palette.secondary.main}
					highlightTitle={"Oops!"}
					subtitle={`Parece que não\nencontramos nenhum\ntrecho cadastrado.`}
				/>
			)}
		</>
	);
}
